import { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { Button1 } from "../index";
import { securityApi } from "../../api/endpoints";

const BRAND_GREEN = "#1BBB6B";

const ERROR_MESSAGES = {
  OTP_REQUIRED: "Please enter the 6-digit code we emailed you.",
  OTP_INVALID: "That code is incorrect. Please try again.",
  OTP_EXPIRED: "That code has expired. Request a new one.",
  OTP_MAX_ATTEMPTS: "Too many wrong attempts. Request a new code.",
  OTP_RESEND_TOO_SOON: "Please wait a moment before requesting another code.",
  PIN_NOT_SET: "Set a transaction PIN first.",
};

/**
 * Generic "prove it's you" step for security-settings changes (removing the
 * PIN, switching verification mode). Emails a PIN_SETUP OTP on open, then
 * calls onConfirm(otpCode) once the user enters it.
 */
const SecurityOtpConfirmModal = ({ open, onClose, title, description, confirmLabel, onConfirm }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const requestedRef = useRef(false);

  const requestCode = async () => {
    setError(null);
    try {
      const res = await securityApi.requestOtp();
      setInfo("We've sent a 6-digit code to your email.");
      setCooldown(res.resendAfterSeconds || 60);
    } catch (err) {
      const apiCode = err.response?.data?.error;
      if (apiCode === "OTP_RESEND_TOO_SOON") {
        setInfo("A code was already sent, check your email.");
        setCooldown(30);
      } else {
        setError(ERROR_MESSAGES[apiCode] || "Could not send the code. Try again.");
      }
    }
  };

  useEffect(() => {
    if (open && !requestedRef.current) {
      requestedRef.current = true;
      requestCode();
    }
    if (!open) {
      requestedRef.current = false;
      setCode("");
      setError(null);
      setInfo(null);
      setCooldown(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleConfirm = async () => {
    if (submitting) return;
    if (!/^\d{6}$/.test(code.trim())) {
      setError(ERROR_MESSAGES.OTP_REQUIRED);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(code.trim());
    } catch (err) {
      const apiCode = err.response?.data?.error;
      setError(ERROR_MESSAGES[apiCode] || apiCode || err.message || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={420} closable>
      <div className="d-flex flex-column gap-3 p-2">
        <span className="fs-5 fw-semibold">{title}</span>
        {description && (
          <span className="fs12px" style={{ color: "#7D7D7D" }}>{description}</span>
        )}
        <span className="fs12px" style={{ color: "#7D7D7D" }}>
          {info || "Sending your verification code..."}
        </span>

        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
          placeholder="______"
          className="form-control text-center fw-bold"
          style={{
            fontSize: 26,
            letterSpacing: 12,
            padding: "12px 8px",
            borderColor: error ? "#FC555B" : BRAND_GREEN,
          }}
        />

        {error && (
          <span className="fs12px" style={{ color: "#FC555B" }}>{error}</span>
        )}

        <div className="d-flex align-items-center justify-content-between">
          <span
            className={`fs12px ${cooldown > 0 ? "" : "cursor-pointer"}`}
            style={{ color: cooldown > 0 ? "#7D7D7D" : BRAND_GREEN }}
            onClick={() => cooldown <= 0 && requestCode()}
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </span>
          <Button1
            buttonName={submitting ? "Confirming..." : confirmLabel || "Confirm"}
            className="btn-dark"
            onClick={handleConfirm}
            disabled={submitting || code.length !== 6}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SecurityOtpConfirmModal;
